/**
 * ConversationCard — an agent conversation as a scannable card.
 *
 * Anatomy (top → bottom):
 *   1. row: [agent badge = colour square + white glyph] + agent name | state pill
 *   2. topic        — the server-generated short title (NOT the raw message)
 *   3. summary      — the session's stored summary (clamp ~2 lines)
 *   4. meta row     — relative time
 *
 * Selected → primary border + soft primary ring (shadow-input-focus token).
 * Newly-shared → amber citation glow.
 *
 * Keeps the existing swipe-to-reveal actions (mobile) and hover actions
 * (desktop): rename / archive / delete / share / assign.
 *
 * Identity is config-driven (agentIdentity). It can be passed in (the parent
 * resolves once for a list) or resolved internally from `session.agent_slug` +
 * the catalogue — so existing callers that pass only `session` keep working.
 */

import type { SessionStatus } from '@/api/types_session'
import { sessionSurfaceSlug } from '@/lib/sessionRouting'
import { useState, useRef, useCallback, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { de, fr } from 'date-fns/locale'
import {
  Pencil,
  Archive,
  Trash2,
  Share2,
  Check,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { prefersReducedMotion } from '@/components/ui/animation-tokens'
import { safeFormatDistanceToNow } from '@/utils/dates'
import { useAgentCatalogue, useAgentLabel } from '@/hooks/useAgentCatalogue'
import { resolveAgentIdentity } from '@/lib/agentIdentity'
import { AgentBadge } from '@/components/AgentBadge'

// Swipe threshold in pixels
const SWIPE_THRESHOLD = 80
// Action button width per button
const BUTTON_WIDTH = 60

/** Conversation lifecycle state driving the status pill. */
export type ConversationState = 'active' | 'done'

/**
 * The card's data contract — tricorder-session's session from the cut-over on:
 * title, the stored summary, the pinned agent's slug, the lifecycle state, and
 * the activity timestamp. The card projections of the old wire (topic,
 * result_summary, primary_source, needs_review, bylines) died with the card
 * pass; a full `SessionResponse` is assignable to this.
 */
export interface ConversationCardSession {
  id: string
  agent?: { slug: string }
  status: SessionStatus
  summary: string
  updated_at: string
  title?: string | null
}

interface ConversationCardProps {
  session: ConversationCardSession
  /** Desktop three-pane: this card is the open conversation. */
  selected?: boolean
  onRename: () => void
  onArchive: () => void
  onDelete: () => void
  onShare?: () => void
  onClick: () => void
}

/** One status-pill descriptor: i18n key + default copy + token classes.
 * Exported so other conversation surfaces (e.g. the handover curation rows)
 * render the SAME pill vocabulary instead of duplicating it. */
export function useStatePill(state: ConversationState) {
  const { t } = useTranslation()
  switch (state) {
    case 'done':
      return {
        label: t('conversation.state.done', 'Erledigt'),
        classes: 'bg-tone-ok-tint text-tone-ok',
        icon: <Check className="h-3 w-3" aria-hidden="true" />,
      }
    case 'active':
    default:
      return {
        label: t('conversation.state.active', 'Aktiv'),
        classes: 'bg-primary-50 text-primary-700',
        // A filled dot (inherits the pill text colour) rather than a glyph.
        icon: (
          <span
            className="h-1.5 w-1.5 rounded-full bg-current"
            aria-hidden="true"
          />
        ),
      }
  }
}

export const ConversationCard = memo(function ConversationCard({
  session,
  selected = false,
  onRename,
  onArchive,
  onDelete,
  onShare,
  onClick,
}: ConversationCardProps) {
  const { t, i18n } = useTranslation()
  const dateLocale = i18n?.language === 'fr' ? fr : de

  // Identity (accent + glyph) resolved from the config catalogue by slug.
  const catalogue = useAgentCatalogue()
  const identity = resolveAgentIdentity(sessionSurfaceSlug(session), catalogue)
  // House rule: the user-facing agent name is localised (German via i18n), with
  // the config name as the fallback — never the raw English specialist title.
  const agentLabel = useAgentLabel()

  const state: ConversationState = session.status === 'archived' ? 'done' : 'active'
  const pill = useStatePill(state)


  // Swipe state
  const [swipeOffset, setSwipeOffset] = useState(0)
  const [isOpen, setIsOpen] = useState(false)
  const touchStartX = useRef<number | null>(null)
  const touchStartY = useRef<number | null>(null)
  const isHorizontalSwipe = useRef(false)

  // Hover state (for desktop)
  const [isHovered, setIsHovered] = useState(false)

  // Calculate action width based on number of buttons. Base 3 (rename,
  // archive, delete) plus the optional share + assign actions.
  const actionCount = 3 + (onShare ? 1 : 0)
  const actionWidth = BUTTON_WIDTH * actionCount

  // Safe date formatting with fallback for iOS Safari compatibility
  const formatTime = (dateString: string) =>
    safeFormatDistanceToNow(dateString, { addSuffix: true, locale: dateLocale }, '–')

  // Touch handlers for swipe
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
    touchStartY.current = e.touches[0].clientY
    isHorizontalSwipe.current = false
  }, [])

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartX.current === null || touchStartY.current === null) return

      const deltaX = touchStartX.current - e.touches[0].clientX
      const deltaY = Math.abs(touchStartY.current - e.touches[0].clientY)

      // Determine if this is a horizontal swipe (require 10px threshold)
      if (!isHorizontalSwipe.current && Math.abs(deltaX) > 10) {
        // Only count as horizontal if vertical movement is less than horizontal
        isHorizontalSwipe.current = deltaY < Math.abs(deltaX)
      }

      if (!isHorizontalSwipe.current) return

      // Prevent default to avoid page scrolling during horizontal swipe
      e.preventDefault()

      // Calculate new offset (only allow left swipe, max = actionWidth)
      const newOffset = isOpen
        ? Math.min(actionWidth, Math.max(0, deltaX + actionWidth))
        : Math.min(actionWidth, Math.max(0, deltaX))

      setSwipeOffset(newOffset)
    },
    [isOpen, actionWidth]
  )

  const handleTouchEnd = useCallback(() => {
    if (!isHorizontalSwipe.current) {
      touchStartX.current = null
      touchStartY.current = null
      return
    }

    // Snap to open or closed based on threshold
    if (swipeOffset >= SWIPE_THRESHOLD) {
      setSwipeOffset(actionWidth)
      setIsOpen(true)
    } else {
      setSwipeOffset(0)
      setIsOpen(false)
    }

    touchStartX.current = null
    touchStartY.current = null
    isHorizontalSwipe.current = false
  }, [swipeOffset, actionWidth])

  // Close swipe when clicking elsewhere
  const handleCardClick = useCallback(() => {
    if (isOpen) {
      setSwipeOffset(0)
      setIsOpen(false)
      return
    }
    onClick()
  }, [isOpen, onClick])

  // Action button handlers (prevent event bubbling)
  const handleAction = useCallback(
    (action: () => void) => (e: React.MouseEvent | React.TouchEvent) => {
      e.stopPropagation()
      setSwipeOffset(0)
      setIsOpen(false)
      action()
    },
    []
  )

  const reduceMotion = prefersReducedMotion()

  const topic = session.title || t('session.newConversation', 'Neues Gespräch')

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cn(
        // The frame carries the border + ring so they are NOT clipped by the
        // overflow-hidden that hides the swipe drawer (overflow clips children,
        // never the element's own box-shadow).
        'relative overflow-hidden rounded-xl border bg-white',
        selected ? 'border-primary shadow-input-focus' : 'border-neutral-200',
      )}
    >
      {/* Action buttons (revealed behind the sliding surface). Hidden at rest
          so they never paint under the card (no bleed-through). */}
      <div
        className={cn(
          'absolute inset-y-0 right-0 flex items-stretch',
          !reduceMotion && 'transition-opacity duration-150',
          swipeOffset > 0 || isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        )}
      >
        {onShare && (
          <button
            onClick={handleAction(onShare)}
            className="flex w-[60px] items-center justify-center bg-primary text-white"
            aria-label={t('session.share', 'Share conversation')}
          >
            <Share2 className="h-5 w-5" />
          </button>
        )}

        <button
          onClick={handleAction(onRename)}
          className="flex w-[60px] items-center justify-center bg-neutral-500 text-white"
          aria-label={t('session.rename')}
        >
          <Pencil className="h-5 w-5" />
        </button>
        <button
          onClick={handleAction(onArchive)}
          className="flex w-[60px] items-center justify-center bg-citation-500 text-white"
          aria-label={t('session.archive')}
        >
          <Archive className="h-5 w-5" />
        </button>
        <button
          onClick={handleAction(onDelete)}
          className="flex w-[60px] items-center justify-center bg-destructive text-white"
          aria-label={t('common.delete')}
        >
          <Trash2 className="h-5 w-5" />
        </button>
      </div>

      {/* Swipeable / pressable content surface */}
      <div
        role="button"
        tabIndex={0}
        onClick={handleCardClick}
        onKeyDown={(e) => e.key === 'Enter' && handleCardClick()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className={cn(
          'relative cursor-pointer select-none bg-white p-3.5 active:bg-neutral-50',
          !reduceMotion && 'transition-transform duration-150'
        )}
        // Only apply a transform while actually swiping — keeping an identity
        // translate at rest would force a permanent compositing layer (and let
        // the action drawer show through in some render paths).
        style={swipeOffset ? { transform: `translateX(${-swipeOffset}px)` } : undefined}
      >
        {/* Row 1 — agent badge + name | state pill */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <AgentBadge identity={identity} size="sm" />
            <span className="truncate text-[11.5px] font-bold text-neutral-900">
              {agentLabel(identity.slug, identity.name)}
            </span>
          </div>
          <span
            className={cn(
              'inline-flex flex-none items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold',
              pill.classes
            )}
          >
            {pill.icon}
            {pill.label}
          </span>
        </div>

        {/* Row 2 — topic */}
        <h3 className="mt-2 line-clamp-1 pr-8 text-[15px] font-bold leading-tight text-neutral-900">
          {topic}
        </h3>

        {/* Row 3 — the session's stored summary */}
        {session.summary && (
          <p className="mt-1 line-clamp-2 text-[13px] leading-[1.45] text-neutral-600">
            {session.summary}
          </p>
        )}

        {/* Row 4 — meta: relative time */}
        <div className="mt-2.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-neutral-400">
          <span>{formatTime(session.updated_at)}</span>
        </div>

        {/* Desktop hover buttons (hidden on touch) */}
        <div
          className={cn(
            'absolute right-2 top-2 flex gap-1',
            'opacity-0 transition-opacity duration-150',
            // Only show on hover when not swiped, hide on touch devices
            isHovered && !isOpen && 'opacity-100',
            'pointer-events-none touch-none md:pointer-events-auto'
          )}
        >
          {onShare && (
            <button
              onClick={handleAction(onShare)}
              className="rounded-lg bg-primary-50 p-2 text-primary-700 hover:bg-primary-100"
              aria-label={t('session.share', 'Share conversation')}
            >
              <Share2 className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={handleAction(onRename)}
            className="rounded-lg bg-neutral-100 p-2 text-neutral-600 hover:bg-neutral-200"
            aria-label={t('session.rename')}
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            onClick={handleAction(onArchive)}
            className="rounded-lg bg-citation-50 p-2 text-citation-700 hover:bg-citation-100"
            aria-label={t('session.archive')}
          >
            <Archive className="h-4 w-4" />
          </button>
          <button
            onClick={handleAction(onDelete)}
            className="rounded-lg bg-error-50 p-2 text-error-700 hover:bg-error-100"
            aria-label={t('common.delete')}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
})
